import React, { useState, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Upload, Scan, Camera, X, Loader2, HelpCircle, MessageSquare, ExternalLink, Star, Globe, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const IdentifyStone = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [showAdvisorForm, setShowAdvisorForm] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const [advisorForm, setAdvisorForm] = useState({
    notes: '',
    contact_name: user?.first_name ? `${user.first_name} ${user.last_name || ''}` : '',
    contact_email: user?.email || '',
    preferred_contact: 'email',
    image_base64: ''
  });

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile && droppedFile.type.startsWith('image/')) {
      processFile(droppedFile);
    }
  }, []);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      // Validate file type
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(selectedFile.type)) {
        toast.error('Unsupported format. Use JPEG, PNG or WEBP.');
        return;
      }
      // Validate file size (max 10MB)
      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error('File too large. Maximum 10MB.');
        return;
      }
      processFile(selectedFile);
    }
  };

  const processFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      setImagePreview(dataUrl);
      // Extract base64 without the data:image/xxx;base64, prefix
      const base64Data = dataUrl.split(',')[1];
      setImageBase64(base64Data);
      setAdvisorForm(prev => ({ ...prev, image_base64: dataUrl }));
    };
    reader.readAsDataURL(file);
    setResults(null);
  };

  const clearFile = () => {
    setImagePreview(null);
    setImageBase64(null);
    setResults(null);
    setScanProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const analyzeImage = async () => {
    if (!imageBase64) {
      toast.error('Please select an image first');
      return;
    }
    
    setLoading(true);
    setScanProgress(0);
    
    // Simulate scanning progress
    const progressInterval = setInterval(() => {
      setScanProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + Math.random() * 15;
      });
    }, 300);
    
    try {
      const response = await axios.post(
        `${API}/identify-stone`,
        { image_base64: imageBase64 },
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true
        }
      );
      
      clearInterval(progressInterval);
      setScanProgress(100);
      
      setTimeout(() => {
        setResults(response.data);
        toast.success('Analysis complete!');
        setLoading(false);
        setScanProgress(0);
      }, 500);
      
    } catch (error) {
      clearInterval(progressInterval);
      console.error('Analysis failed:', error);
      toast.error(error.response?.data?.detail || 'Analysis failed. Please try again.');
      setLoading(false);
      setScanProgress(0);
    }
  };

  const submitAdvisorRequest = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/advisor-requests`, advisorForm, { withCredentials: true });
      toast.success('Request submitted! A Stone Advisor will review your image and contact you.');
      setShowAdvisorForm(false);
    } catch (error) {
      toast.error('Failed to submit request');
    }
  };

  const getConfidenceColor = (confidence) => {
    if (!confidence) return 'text-muted-foreground';
    const lower = confidence.toLowerCase();
    if (lower === 'high') return 'text-green-600';
    if (lower === 'medium') return 'text-yellow-600';
    return 'text-red-500';
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background border-b border-border">
        <div className="max-w-[1800px] mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="heading-display text-xl font-medium">Stone Platform</Link>
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/dashboard" className="nav-link">Dashboard</Link>
              <Link to="/materials" className="nav-link">Materials</Link>
              <Link to="/identify" className="nav-link active">Identify</Link>
              <Link to="/boards" className="nav-link">Boards</Link>
            </nav>
            <div className="w-20"></div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 md:px-12 py-12 md:py-24">
        <div className="text-center mb-12">
          <p className="caption-text text-muted-foreground mb-2">AI Stone Recognition</p>
          <h1 className="heading-display text-4xl md:text-5xl font-medium mb-4" data-testid="identify-title">
            Identify Natural Stone
          </h1>
          <p className="body-text text-muted-foreground max-w-xl mx-auto">
            Upload a photo of any stone surface. Our AI will analyze it and identify the material, providing matching options from our collection.
          </p>
        </div>

        {/* Upload Area */}
        {!imagePreview ? (
          <div
            className={`border-2 border-dashed ${dragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/30'} p-12 text-center cursor-pointer hover:border-muted-foreground/50 transition-colors bg-secondary/20`}
            onClick={() => fileInputRef.current?.click()}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            data-testid="upload-dropzone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileSelect}
              className="hidden"
              id="file-upload"
              data-testid="file-input"
            />
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 flex items-center justify-center bg-secondary rounded-full mb-6">
                <Upload className="w-10 h-10 text-muted-foreground" />
              </div>
              <p className="heading-display text-2xl font-medium mb-2">Drop your image here</p>
              <p className="text-muted-foreground mb-6">or click to browse</p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-widest">
                <Camera className="w-4 h-4" />
                <span>JPEG, PNG, WEBP • Max 10MB</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Preview with scanning effect */}
            <div className="bg-white border border-border overflow-hidden">
              <div className="relative aspect-[16/10] bg-secondary">
                <img 
                  src={imagePreview} 
                  alt="Uploaded stone" 
                  className="w-full h-full object-contain"
                  data-testid="image-preview"
                />
                
                {/* Scanning overlay */}
                {loading && (
                  <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center overflow-hidden">
                    {/* Scan line animation */}
                    <div 
                      className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-primary to-transparent"
                      style={{
                        animation: 'scanLine 2s ease-in-out infinite',
                        top: `${scanProgress}%`
                      }}
                    />
                    <style>{`
                      @keyframes scanLine {
                        0%, 100% { transform: translateY(-100%); opacity: 0; }
                        50% { transform: translateY(0); opacity: 1; }
                      }
                    `}</style>
                    
                    <div className="relative z-10 text-center">
                      <div className="w-16 h-16 mx-auto mb-4 relative">
                        <Scan className="w-full h-full text-primary animate-pulse" />
                      </div>
                      <p className="text-white heading-display text-2xl mb-2">Analyzing stone...</p>
                      <p className="text-muted-foreground text-sm">{Math.round(scanProgress)}%</p>
                    </div>
                  </div>
                )}
                
                {/* Clear button */}
                {!loading && (
                  <button
                    onClick={clearFile}
                    className="absolute top-4 right-4 p-2 bg-white/90 hover:bg-white transition-colors"
                    data-testid="clear-image-btn"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>

              {/* Actions */}
              {!results && (
                <div className="p-6 border-t border-border">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button 
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={loading}
                      className="flex-1 py-6"
                      data-testid="change-image-btn"
                    >
                      <Camera className="w-5 h-5 mr-2" />
                      Change Image
                    </Button>
                    <Button 
                      onClick={analyzeImage} 
                      disabled={loading}
                      className="flex-1 py-6 bg-primary hover:bg-primary/90"
                      data-testid="analyze-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Scan className="w-5 h-5 mr-2" />
                          Identify Stone
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Results */}
            {results && (
              <div className="space-y-8 animate-fade-in-up">
                {/* Main Analysis Card */}
                <div className="bg-white border border-border p-6 md:p-8">
                  <div className="flex items-center justify-between mb-6">
                    <p className="caption-text text-muted-foreground uppercase tracking-widest">Stone Identification</p>
                    <span className={`flex items-center gap-1 text-sm font-medium ${getConfidenceColor(results.confidence)}`}>
                      <CheckCircle className="w-4 h-4" />
                      {results.confidence} confidence
                    </span>
                  </div>
                  
                  <h2 className="heading-display text-3xl font-medium mb-2" data-testid="result-name">
                    {results.commercial_name || 'Unknown Stone'}
                  </h2>
                  <p className="text-lg text-muted-foreground mb-6">{results.stone_type}</p>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
                    {results.color && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wide">Color</p>
                        <p className="font-medium">{results.color}</p>
                      </div>
                    )}
                    {results.veining && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wide">Veining</p>
                        <p className="font-medium">{results.veining}</p>
                      </div>
                    )}
                    {results.origin_country && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wide">Origin</p>
                        <p className="font-medium flex items-center gap-1">
                          <Globe className="w-3 h-3" />
                          {results.origin_country}
                        </p>
                      </div>
                    )}
                    {results.recommended_use && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wide">Applications</p>
                        <p className="font-medium capitalize">{results.recommended_use}</p>
                      </div>
                    )}
                  </div>
                  
                  {results.characteristics && (
                    <div className="border-t border-border pt-6">
                      <p className="text-xs text-muted-foreground mb-2 uppercase tracking-wide">Characteristics</p>
                      <p className="text-muted-foreground">{results.characteristics}</p>
                    </div>
                  )}
                  
                  {results.distinguishing_features && (
                    <div className="mt-4">
                      <p className="text-xs text-muted-foreground mb-2 uppercase tracking-wide">Distinguishing Features</p>
                      <p className="text-muted-foreground">{results.distinguishing_features}</p>
                    </div>
                  )}
                </div>

                {/* Similar Stones - Clickable with preview */}
                {results.similar_stones?.length > 0 && (
                  <div className="bg-secondary/30 p-6 md:p-8">
                    <p className="text-xs text-muted-foreground mb-3 uppercase tracking-wide">Similar Stones</p>
                    <div className="flex flex-wrap gap-2">
                      {results.similar_stones.map((stone, idx) => (
                        <Link 
                          key={idx} 
                          to={`/materials?search=${encodeURIComponent(stone)}`}
                          className="px-3 py-1 bg-white text-sm hover:bg-primary hover:text-primary-foreground transition-colors cursor-pointer"
                        >
                          {stone}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Matches from Library */}
                {results.matches?.length > 0 && (
                  <div>
                    <p className="caption-text text-muted-foreground mb-6 uppercase tracking-widest">Matches in Library</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {results.matches.map((material) => (
                        <Link
                          key={material.material_id}
                          to={`/materials/${material.material_id}`}
                          className="group bg-white border border-border overflow-hidden hover:border-foreground transition-colors"
                          data-testid={`match-${material.material_id}`}
                        >
                          <div className="aspect-square overflow-hidden bg-secondary">
                            <img 
                              src={material.images?.[0] || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400'} 
                              alt={material.name}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                          </div>
                          <div className="p-4">
                            <p className="caption-text text-muted-foreground text-xs mb-1">{material.stone_type}</p>
                            <h3 className="heading-display text-lg font-medium">{material.name}</h3>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-4 pt-8 border-t border-border">
                  <Button 
                    variant="outline" 
                    onClick={clearFile}
                    className="flex-1 py-6"
                  >
                    <Camera className="w-5 h-5 mr-2" />
                    Identify Another Stone
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => setShowAdvisorForm(true)}
                    className="flex-1 py-6"
                    data-testid="ask-advisor-btn"
                  >
                    <MessageSquare className="w-5 h-5 mr-2" />
                    Ask a Material Advisor
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tips Section */}
        {!imagePreview && (
          <div className="mt-12 grid sm:grid-cols-3 gap-1 bg-border" data-testid="tips-section">
            <div className="bg-white p-6">
              <p className="text-xs uppercase tracking-widest text-primary mb-2 font-semibold">Tip 1</p>
              <p className="text-muted-foreground text-sm">Photograph the slab with good lighting, avoiding reflections</p>
            </div>
            <div className="bg-white p-6">
              <p className="text-xs uppercase tracking-widest text-primary mb-2 font-semibold">Tip 2</p>
              <p className="text-muted-foreground text-sm">Focus on the veining and characteristic details of the stone</p>
            </div>
            <div className="bg-white p-6">
              <p className="text-xs uppercase tracking-widest text-primary mb-2 font-semibold">Tip 3</p>
              <p className="text-muted-foreground text-sm">Use high-resolution images for better results</p>
            </div>
          </div>
        )}

        {/* Advisor Dialog */}
        <Dialog open={showAdvisorForm} onOpenChange={setShowAdvisorForm}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="heading-display text-xl">Ask a Material Advisor</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              Our expert will review your image and help identify the stone.
            </p>
            <form onSubmit={submitAdvisorRequest} className="space-y-4 mt-4">
              <div>
                <Label className="caption-text mb-2 block">Name</Label>
                <Input
                  value={advisorForm.contact_name}
                  onChange={(e) => setAdvisorForm({...advisorForm, contact_name: e.target.value})}
                  className="input-field"
                  required
                />
              </div>
              <div>
                <Label className="caption-text mb-2 block">Email</Label>
                <Input
                  type="email"
                  value={advisorForm.contact_email}
                  onChange={(e) => setAdvisorForm({...advisorForm, contact_email: e.target.value})}
                  className="input-field"
                  required
                />
              </div>
              <div>
                <Label className="caption-text mb-2 block">Additional Notes</Label>
                <Textarea
                  value={advisorForm.notes}
                  onChange={(e) => setAdvisorForm({...advisorForm, notes: e.target.value})}
                  className="input-field min-h-[100px]"
                  placeholder="Where did you see this stone? Any other details?"
                  required
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowAdvisorForm(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1">
                  Submit Request
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default IdentifyStone;
